<template>
  <div class="category-page">

    <!-- Page title (single-category mode or index mode) -->
    <h1 class="category-page__title" v-if="category.name">{{ category.name }}</h1>

    <!-- ── INDEX VIEW: multiple categories, each as a scrollable rail ── -->
    <div class="category-page__rails" v-if="categories.length > 1">
      <section
        v-for="cat in categories"
        :key="cat.id"
        class="rail"
        :class="{ 'rail--top10': cat.id === 195 }"
        :aria-label="cat.name"
      >
        <div class="rail__header">
          <h2 class="rail__title">{{ cat.name }}</h2>
        </div>

        <div class="rail__scroll-wrap" :class="{ 'is-hovering': cat._hovering }">
           <button
            class="rail__nav rail__nav--prev"
            type="button"
            aria-label="הקודם"
            :aria-controls="'rail-list-' + cat.id"
            @click="scrollRail($event, -1)"
          >   
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>
       
           </button>
          <button
            class="rail__nav rail__nav--next"
            type="button"
            aria-label="הבא"
            :aria-controls="'rail-list-' + cat.id"
            @click="scrollRail($event, 1)"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <ul
            :id="'rail-list-' + cat.id"
            class="rail__list"
            :class="{ 'is-hovering': cat._hovering }"
            role="list"
            @mouseenter="cat._hovering = true"
            @mouseleave="cat._hovering = false"
          >
            <item-tpl
              v-for="item in cat.items"
              :key="item.id"
              :item="item"
            />
          </ul>
          <div class="rail__fade" aria-hidden="true"></div>
        </div>
      </section>
    </div>

    <!-- ── SINGLE-CATEGORY VIEW: CSS grid of all items ── -->
    <section class="category-page__grid" v-if="categories.length === 1" aria-label="כל הפריטים">
      <item-tpl
        v-for="item in activeItems"
        :key="item.id"
        :item="item"
      />
    </section>

  </div>
</template>

<script>
import itemTpl from './item_tpl.vue';

export default {
  components: { itemTpl },

  data() {
    return {
      promoted: [],
      categories: [],
      category: [],
      id: this.$route.params.id,
      type: this.$route.params.type,
      wordType: ['movie', 'series'],
    };
  },

  computed: {
    activeItems() {
      let typein = this.type;
      let movietypein;
      if (typein) {
        movietypein = this.wordType[typein - 1];
      }
      return this.categories[0].items.filter(function (categoryType) {
        if (movietypein)
          return categoryType.type === movietypein;
        else
          return categoryType.type === 'movie' || categoryType.type === 'series';
      });
    },
  },

  methods: {
    scrollRail(event, dir) {
      const btn = event.currentTarget;
      const list = btn.parentElement.querySelector('.rail__list');
      if (!list) return;
      const isRtl = getComputedStyle(list).direction === 'rtl';
      const sign = isRtl ? -dir : dir;
      list.scrollBy({ left: list.clientWidth * 0.8 * sign, behavior: 'smooth' });
    },
  },

  updated() {
    window.dispatchEvent(new Event('resize'));
  },

  created() {
    this.$emit('show_loader');
    var self = this;

    this.api_call({
      url: 'category',
      data: { id: this.id, type: self.wordType[this.type - 1] },
      onComplete: function (data) {
        for (var i in data.data.data.promoted) {
          data.data.data.promoted[i].is_active = i == 0;
        }
        self.promoted = data.data.data.promoted;
        var cats = data.data.data.categories;
        for (var ci in cats) { cats[ci]._hovering = false; }
        self.categories = cats;
        self.category = data.data.data.category;
        self.$emit('loaded');
      },
    });
  },
};
</script>

<style scoped lang="scss">
/* ── Page wrapper ── */
.category-page {
  background: var(--color-bg);
  min-height: 100vh;
  padding-block-start: var(--space-8);
  padding-block-end: var(--space-12);
}

/* ── Page title — title/lg per spec §1.2 ── */
.category-page__title {
  font-size: 28px;
  font-weight: 700;
  line-height: 34px;
  letter-spacing: -0.4px;
  color: var(--color-text);
  font-family: var(--font-hebrew);
  text-align: center;
  margin-block-end: var(--space-8);
  margin-block-start: 0;
  padding-inline: var(--space-8);
}

/* ── Rails container ── */
.category-page__rails {
  padding-block-end: var(--space-12);
}

/* ── Rail ── */
.rail {
  margin-block-end: 56px;
}

/* Rail header */
.rail__header {
  display: flex;
  align-items: baseline;
  padding: 0 36px 14px;
}

.rail__title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.3px;
  color: var(--color-text);
  font-family: var(--font-hebrew);
  text-align: start;
  transition: font-size 200ms ease, text-shadow 200ms ease;
}
.rail:hover .rail__title {
  font-size: 23px;
  text-shadow: 0 0 8px rgba(255,255,255,0.1);
}

/* Header sits above scaled hover cards */
.rail__header {
  position: relative;
  z-index: 12;
}

/* Scroll wrapper — contains list + fade */
.rail__scroll-wrap {
  position: relative;
}

/* Card list — horizontally-scrollable flex row */
.rail__list {
  display: flex;
  gap: var(--rail-gap, 6px);
  padding: 50px var(--rail-padding-inline, 36px) 50px;
  margin-block: -50px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scroll-snap-type: x proximity;
  overflow: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  list-style: none;
}
.rail__list::-webkit-scrollbar {
  display: none;
}

/* Fade mask on the trailing edge (RTL: inline-end = left side) */


/* ── Rail nav arrows — always visible carousel chevrons ── */
.rail__nav {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 56px;
  height: auto;
  transform: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 0;
  cursor: pointer;
  z-index: 20;
  opacity: 0.85;
  pointer-events: auto;
  transition: opacity 180ms ease, background 180ms ease;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
}
.rail__nav--prev {
  right: 0;
  left: auto;
  background: linear-gradient(to left, rgba(0, 0, 0, 0.65) 20%, transparent 100%);
}
.rail__nav--next {
  left: 0;
  right: auto;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.65) 20%, transparent 100%);
}
.rail__nav svg {
  width: 24px;
  height: 24px;
}
.rail__nav:hover {
  opacity: 1;
}
.rail__nav:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: -4px;
}
@media (hover: none) {
  .rail__nav {
    display: none;
  }
}

/* ── Rail dim ── */
.rail__list.is-hovering :deep(.poster-card) {
  opacity: 0.55;
  transition: opacity 200ms ease;
}
.rail__list.is-hovering :deep(.poster-card:hover) {
  opacity: 1;
}

/* ── Single-category grid — sized for 16:9 thumbs ── */
.category-page__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  column-gap: var(--space-4);
  row-gap: 48px;
  padding: var(--space-6) var(--space-8) var(--space-12);
}
/* Override rail-formula width so cards fill grid cell */
.category-page__grid > :deep(.poster-card),
.category-page__grid :deep(.poster-card__inner) {
  width: 100%;
  max-width: none;
  min-width: 0;
}

/* ── Mobile adjustments ── */
@media (max-width: 640px) {
  .rail__header {
    padding: 0 var(--rail-padding-inline, 16px) 12px;
  }
  .rail__title {
    font-size: 17px;
  }
  .rail:hover .rail__title {
    font-size: 18px;
  }
  .category-page__grid {
    padding: var(--space-4) var(--space-4) var(--space-8);
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    column-gap: var(--space-3);
    row-gap: 32px;
  }
  .category-page__title {
    font-size: 22px;
    padding-inline: var(--space-4);
  }
}
</style>
